import React, { useEffect, useRef, useState } from 'react';
import { heroVideo, brand } from '../../data/mock';
import { ChevronDown } from 'lucide-react';
import { IMAGE_SIZES, responsiveImageProps } from '../../lib/images';

const Hero = ({ onBook }) => {
  const videoRef = useRef(null);
  const [canLoadVideo, setCanLoadVideo] = useState(false);
  const [ready, setReady] = useState(false);

  useEffect(() => {
    const startVideoLoad = () => setCanLoadVideo(true);
    if ('requestIdleCallback' in window) {
      const idleId = window.requestIdleCallback(startVideoLoad, { timeout: 1200 });
      return () => window.cancelIdleCallback(idleId);
    }
    const timer = window.setTimeout(startVideoLoad, 600);
    return () => window.clearTimeout(timer);
  }, []);

  useEffect(() => {
    if (!canLoadVideo) return;
    const v = videoRef.current;
    if (!v) return;
    const onCanPlay = () => setReady(true);
    const playVideo = () => v.play().catch(() => {});
    v.addEventListener('canplay', onCanPlay);
    playVideo();

    let io;
    if ('IntersectionObserver' in window) {
      io = new IntersectionObserver(([entry]) => {
        if (entry.isIntersecting) {
          playVideo();
        } else {
          v.pause();
        }
      }, { threshold: 0.2 });
      io.observe(v);
    }

    return () => {
      v.removeEventListener('canplay', onCanPlay);
      if (io) io.disconnect();
    };
  }, [canLoadVideo]);

  return (
    <section id="top" className="relative h-[100svh] min-h-[620px] w-full overflow-hidden bg-black">
      <img
        {...responsiveImageProps(heroVideo.poster, IMAGE_SIZES.full)}
        alt=""
        loading="eager"
        fetchPriority="high"
        className={`absolute inset-0 w-full h-full object-cover transition-opacity duration-1000 ${ready ? 'opacity-0' : 'opacity-100'}`}
      />
      {canLoadVideo && (
        <video
          ref={videoRef}
          className={`absolute inset-0 w-full h-full object-cover transition-opacity duration-1000 ${ready ? 'opacity-100' : 'opacity-0'}`}
          src={heroVideo.src}
          poster={heroVideo.poster}
          autoPlay
          muted
          loop
          playsInline
          preload="metadata"
        />
      )}
      <div className="absolute inset-0 bg-gradient-to-b from-black/45 via-black/15 to-black/55" />

      <div className="relative z-10 h-full max-w-[1440px] mx-auto px-5 md:px-10 flex flex-col justify-end pb-24 md:pb-28">
        <div className="max-w-3xl text-white">
          <div className="tracking-[0.4em] text-[11px] md:text-[12px] text-white/80 mb-6">
            A NEW ADDRESS ON THE BOSPHORUS
          </div>
          <h1 className="font-serif text-[44px] leading-[1.04] sm:text-6xl md:text-[76px] lg:text-[88px] md:leading-[0.98] tracking-[-0.01em]">
            {brand.name}
            <br />
            <span className="text-white/85">{brand.location}</span>
          </h1>
          <p className="mt-6 md:mt-8 max-w-xl text-white/85 text-[15px] md:text-[17px] leading-relaxed font-light">
            Where centuries of Ottoman craft meet the quiet luxury of the modern journey — on the shore of the strait that has moved the world.
          </p>
          <div className="mt-8 md:mt-10 flex flex-wrap gap-3">
            <button
              onClick={onBook}
              className="px-7 h-12 bg-white text-neutral-900 tracking-[0.22em] text-[11px] uppercase hover:bg-white/85 transition-colors"
            >
              Book Your Stay
            </button>
            <a
              href="#stay"
              className="px-7 h-12 border border-white/70 text-white tracking-[0.22em] text-[11px] uppercase hover:bg-white hover:text-neutral-900 transition-colors inline-flex items-center"
            >
              Discover The Hotel
            </a>
          </div>
        </div>
      </div>

      <a href="#introduction" className="absolute z-10 bottom-6 left-1/2 -translate-x-1/2 text-white/85 hover:text-white transition-colors">
        <ChevronDown className="w-6 h-6 animate-bounce" strokeWidth={1.25} />
      </a>
    </section>
  );
};

export default Hero;
